import { forwardRef, useState } from 'react';
import clsx from 'clsx';
import Input from './input.jsx';
import Label from './label.jsx';

// A text input instead of type="number": Chromium reports value "" for a partial entry such as "0.",
// which used to clear the field mid-typing (0.032 ended up as 32). Both "." and "," are decimal separators.
const PARTIAL_NUMBER = /^\d*([.,]\d*)?$/;

const formatNumber = value => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return Number(value).toString();
};

const sanitize = value => {
  if (value === '' || value === null || value === undefined) {
    return '';
  }
  const parsed = Number(String(value).replace(',', '.'));
  if (Number.isNaN(parsed)) {
    return '';
  }
  return parsed;
};

const NumberInput = forwardRef(function NumberInput(
  { id, label, helperText, error, min = 0, step = 'any', className, onValueChange, onBlur, value, ...props },
  ref,
) {
  const [draft, setDraft] = useState(null);
  // Keep what the operator typed ("0.", "1,2") while it still means the current value; an outside change wins.
  const shown = draft !== null && sanitize(draft) === sanitize(value) ? draft : formatNumber(value);

  return (
    <div className={clsx('flex w-full flex-col', className)}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <Input
        id={id}
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={shown}
        onChange={event => {
          const raw = event.target.value.trim();
          if (!PARTIAL_NUMBER.test(raw)) return;
          setDraft(raw);
          onValueChange && onValueChange(sanitize(raw));
        }}
        onBlur={event => {
          setDraft(null);
          onBlur && onBlur(event);
        }}
        {...props}
      />
      {error ? (
        <span className="mt-1 text-xs font-medium text-[rgb(var(--brand-700))]">{error}</span>
      ) : (
        helperText && <span className="mt-1 text-xs text-[rgba(var(--muted),0.9)]">{helperText}</span>
      )}
    </div>
  );
});

export default NumberInput;
